import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  authService,
  type SignUpCredentials,
  type SignInCredentials,
} from "@/services/supabase/auth";
import type { User, Session } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      setIsLoading(true);
      try {
        const { data } = await authService.getSession();
        setUser(data.session?.user || null);
        setSession(data.session || null);
      } catch (error) {
        console.error("Error getting session:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getInitialSession();

    // Set up auth listener
    const {
      data: { subscription },
    } = authService.onAuthStateChange((event, session) => {
      const currentUser = session?.user || null;
      setUser(currentUser);
      setSession(session || null);

      // Clear queries when signing out
      if (event === "SIGNED_OUT") {
        queryClient.clear();
        setUser(null);
        setSession(null);
      }

      // Invalidate user-specific queries when auth state changes
      if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        queryClient.invalidateQueries({ queryKey: ["user"] });
        queryClient.invalidateQueries({ queryKey: ["profile"] });
      }
    });

    // Clean up subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const signInWithEmail = useMutation({
    mutationFn: authService.signInWithEmail,
    onError: (error: Error) => {
      toast({
        title: "Sign in failed",
        description: error.message || "There was a problem signing in.",
        variant: "destructive",
      });
    },
  });

  const signUpWithEmail = useMutation({
    mutationFn: authService.signUpWithEmail,
    onError: (error: Error) => {
      toast({
        title: "Sign up failed",
        description:
          error.message || "There was a problem creating your account.",
        variant: "destructive",
      });
    },
  });

  const signOut = useMutation({
    mutationFn: authService.signOut,
    onSuccess: () => {
      // Clear all query cache on sign out
      queryClient.clear();
    },
    onError: (error: Error) => {
      toast({
        title: "Sign out failed",
        description: error.message || "There was a problem signing out.",
        variant: "destructive",
      });
    },
  });

  const resetPassword = useMutation({
    mutationFn: authService.resetPassword,
    onSuccess: () => {
      toast({
        title: "Password reset email sent",
        description: "Check your email for a password reset link.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Password reset failed",
        description:
          error.message || "There was a problem sending the reset email.",
        variant: "destructive",
      });
    },
  });

  const updatePassword = useMutation({
    mutationFn: authService.updatePassword,
    onSuccess: () => {
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Password update failed",
        description:
          error.message || "There was a problem updating your password.",
        variant: "destructive",
      });
    },
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    resetPassword,
    updatePassword,
    session,
  };
};

export function useAdminCheck(userId?: string) {
  return useQuery({
    queryKey: ["adminStatus", userId],
    queryFn: () => authService.isAdmin(userId!),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useSuperadminCheck(userId?: string) {
  return useQuery({
    queryKey: ["superadminStatus", userId],
    queryFn: () => authService.checkSuperadminStatus(userId!),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
